// ABOUTME: Displays adult family members grid with add/edit/remove functionality
// ABOUTME: Shows avatar, role, email, last login, and allows role editing for non-owners

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Button,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as CrownIcon
} from '@mui/icons-material';
import type { FamilyMember, FamilyDetailData } from './types';

interface AdultsSectionProps {
  family: FamilyDetailData;
  memberCustomRoles: Record<string, string>;
  formatRelativeTime: (dateString?: string) => string;
  onAddAdult: () => void;
  onMemberClick: (memberId: number) => void;
  onEditRole: (userId: number, currentRole: string, familyId: number) => void;
  onRemoveMember: (member: FamilyMember) => void;
}

const AdultsSection: React.FC<AdultsSectionProps> = ({
  family,
  memberCustomRoles,
  formatRelativeTime,
  onAddAdult,
  onMemberClick,
  onEditRole,
  onRemoveMember
}) => {
  return (
    <Card sx={{ mb: 3 }} data-testid="adults-section">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Adults ({family.adults.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddAdult}
          >
            Add Adult
          </Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {family.adults.map((adult) => (
            <Card
              key={adult.id}
              variant="outlined"
              sx={{ cursor: 'pointer' }}
              onClick={() => onMemberClick(adult.id)}
              data-testid={`adult-card-${adult.id}`}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar src={adult.avatarUrl} sx={{ mr: 2 }}>
                    {adult.firstName[0]}{adult.lastName[0]}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {adult.firstName} {adult.lastName}
                      </Typography>
                      {adult.role === 'Owner' && (
                        // Icon glyph sizing via fontSize, not a Typography text override
                        <CrownIcon sx={{ color: 'gold', fontSize: '1rem' }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={adult.role === 'Owner' ? `${memberCustomRoles[adult.id] || 'Loading...'} (Primary)` : (memberCustomRoles[adult.id] || 'Loading...')}
                        size="small"
                        color={adult.role === 'Owner' ? 'secondary' : 'primary'}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditRole(adult.id, memberCustomRoles[adult.id] || '', family.id);
                        }}
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                      >
                        Edit
                      </Button>
                      {adult.role !== 'Owner' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveMember(adult);
                          }}
                          title="Remove family member"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {adult.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last Login: {formatRelativeTime(adult.lastLogin)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdultsSection;
